import React, { useState } from 'react';
import { Plus, Trash2, Phone, Mail, MessageCircle } from 'lucide-react';
import Can from './Can';
import { P } from '../../constants/permissions';

const ContactManager = ({ contacts = [], onChange }) => {
  const [newContact, setNewContact] = useState({
    contact_type: 'email',
    value: '',
    description: '',
    is_primary: false
  });

  const contactTypes = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'phone', label: 'Telefone', icon: Phone },
    { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
    { value: 'telegram', label: 'Telegram', icon: MessageCircle },
    { value: 'linkedin', label: 'LinkedIn', icon: Mail },
    { value: 'website', label: 'Website', icon: Mail },
    { value: 'address', label: 'Endereço', icon: Mail }
  ];

  const handleAddContact = () => {
    if (!newContact.value.trim()) return;

    const contact = {
      ...newContact,
      id: `temp_${Date.now()}`
    };

    onChange([...contacts, contact]);
    setNewContact({
      contact_type: 'email',
      value: '',
      description: '',
      is_primary: false
    });
  };

  const handleRemoveContact = (index) => {
    const updatedContacts = contacts.filter((_, i) => i !== index);
    onChange(updatedContacts);
  };

  const handleUpdateContact = (index, field, value) => {
    const updatedContacts = [...contacts];
    updatedContacts[index] = { ...updatedContacts[index], [field]: value };
    onChange(updatedContacts);
  };

  const getContactIcon = (type) => {
    const contactType = contactTypes.find(ct => ct.value === type);
    return contactType ? contactType.icon : Mail;
  };

  const getContactLabel = (type) => {
    const contactType = contactTypes.find(ct => ct.value === type);
    return contactType ? contactType.label : type;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Contatos</h3>
        <span className="text-sm text-gray-500">{contacts.length} contato(s)</span>
      </div>

      {/* Lista de contatos existentes */}
      {contacts.length > 0 && (
        <div className="space-y-2">
          {contacts.map((contact, index) => {
            const Icon = getContactIcon(contact.contact_type);
            return (
              <div key={contact.id || index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Icon className="w-4 h-4 text-gray-600" />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{contact.value}</span>
                    {contact.is_primary && (
                      <span className="badge badge-primary badge-sm">Principal</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {getContactLabel(contact.contact_type)}
                    {contact.description && ` - ${contact.description}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={contact.is_primary}
                      onChange={(e) => handleUpdateContact(index, 'is_primary', e.target.checked)}
                      className="form-checkbox"
                    />
                    Principal
                  </label>
                  <Can permission={P.IDENTITY_ACCESS}>
                    <button
                      onClick={() => handleRemoveContact(index)}
                      className="btn btn-danger btn-sm"
                      type="button"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </Can>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Formulário para adicionar novo contato */}
      <div className="border-t pt-4">
        <h4 className="text-md font-medium text-gray-900 mb-3">Adicionar Contato</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="form-label">Tipo de Contato</label>
            <select
              value={newContact.contact_type}
              onChange={(e) => setNewContact({ ...newContact, contact_type: e.target.value })}
              className="form-select"
            >
              {contactTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="form-label">Valor</label>
            <input
              type="text"
              value={newContact.value}
              onChange={(e) => setNewContact({ ...newContact, value: e.target.value })}
              placeholder="Ex: joao@email.com ou (11) 99999-9999"
              className="form-input"
            />
          </div>

          <div className="md:col-span-2">
            <label className="form-label">Descrição (opcional)</label>
            <input
              type="text"
              value={newContact.description}
              onChange={(e) => setNewContact({ ...newContact, description: e.target.value })}
              placeholder="Ex: Contato comercial, WhatsApp pessoal"
              className="form-input"
            />
          </div>

          <div className="md:col-span-2 flex items-center justify-between">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newContact.is_primary}
                onChange={(e) => setNewContact({ ...newContact, is_primary: e.target.checked })}
                className="form-checkbox"
              />
              <span className="text-sm text-gray-700">Marcar como contato principal</span>
            </label>

            <Can permission={P.IDENTITY_ACCESS}>
              <button
                onClick={handleAddContact}
                disabled={!newContact.value.trim()}
                className="btn btn-primary"
                type="button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Adicionar Contato
              </button>
            </Can>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactManager;
